
import { defineComponent, ref, computed, watch } from "vue-demi";
import { debounce } from "throttle-debounce-ts";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMApp, SMTasks, SMValidationModal } from "@/store";
import { IValidationForm } from "@/models/validation";
import { app } from "@/main";

const MIN_QUERY_LENGTH = 3;

export default defineComponent({
  name: "OnboardingModal",

  props: {
    isPrepaidSL: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const modelValuePopupBanner = computed({
      get: () => SMValidationModal.isOpen,
      set: (value) => SMValidationModal.set(value),
    });

    const modelValuePopupSuccess = ref(false);

    const title = computed(() => {
      if (props.isPrepaidSL)
        return "Welcome! You're All Set to Start Your Sponsor Licence Application";

      return "Welcome to Nation.better!";
    });

    const descriptions = computed(() => {
      if (props.isPrepaidSL)
        return [
          `Thanks for choosing <b>Nation.better</b> to guide you through the Sponsor Licence process.`,
          "This service is already included in your package - no extra steps or payments required.",
          "We’ll walk you through every stage, from gathering documents to final submission, with helpful instructions and platform tools to keep you on track and avoid delays.",
        ];

      return [
        `Hire the best world talent and stay compliant with the Home Office regulations effortlessly with our step-by-step guidance, immigration support, and automation.`,
        `<b>Note:</b> We only support Skilled Worker visa route at the moment.`,
      ];
    });

    const label = computed(() => {
      return "To get started, please confirm your company’s legal name:";
    });

    const companyName = ref("");

    const query = ref("");
    const items = ref([]);
    const loading = ref(false);
    const buttonLoading = ref(false);

    const debouncedFetch = debounce(500, async () => {
      try {
        const { data } = await axios.get(API_ROUTES.GET_COMPANY, {
          params: {
            q: encodeURIComponent(query.value),
          },
        });

        if (!data?.items) {
          items.value = [];

          return;
        }

        items.value = data.items.map((v) => ({
          label: v.title,
          value: v.value,
        }));
      } finally {
        loading.value = false;
      }
    });

    const companyNameValid = ref(true);

    const fetchItems = () => {
      loading.value = true;

      debouncedFetch();
    };

    const filteredItems = computed(() => {
      return items.value;
    });

    async function validateCompanyName(companyName: string) {
      try {
        const { data } = await axios.get(API_ROUTES.VALIDATE_COMPANY_NAME(companyName));

        companyNameValid.value = data;
      } catch {
        return;
      }
    }

    const rules = computed(() => {
      const isValid = companyNameValid.value;

      return [
        () =>
          isValid ||
          "This company is already registered. Please contact your company representative to get access.",
      ];
    });

    watch(companyName, () => {
      companyNameValid.value = true;
    });

    const dropdownRef = ref(null);

    watch(companyNameValid, () => {
      dropdownRef.value?.validate?.();
    });

    const selectedCompanyData = ref({
      label: "",
      value: "",
    });

    watch(query, async (val) => {
      const chosenItem = filteredItems.value.find((v) => v.label === val);

      if (chosenItem) {
        selectedCompanyData.value = chosenItem;

        await validateCompanyName(chosenItem.label);

        items.value = [chosenItem];

        return;
      }

      if (val.length >= MIN_QUERY_LENGTH && !loading.value) {
        items.value = [];

        fetchItems();
      }
    });

    function onSearch(value: string) {
      query.value = value;
    }

    function onClear() {
      items.value = [];
    }

    function onNoMatchSelect() {
      selectedCompanyData.value.label = companyName.value;
      selectedCompanyData.value.value = "";
    }

    const ifSponsor = ref(null);

    async function onSuccess() {
      try {
        buttonLoading.value = true;

        const company_name = selectedCompanyData.value?.label || "";

        const ch_number = selectedCompanyData.value?.value
          ? selectedCompanyData.value.value.trim()
          : "";

        const data: IValidationForm = {
          if_sponsor: !!ifSponsor.value,
          ch_number,
          company_name,
        };

        const url = API_ROUTES.STEPS_VALIDATION;

        await axios.post(url, data);

        SMValidationModal.close();

        await SMTasks.getTableTasksToDo();

        modelValuePopupSuccess.value = true;
      } finally {
        buttonLoading.value = false;
      }
    }

    async function updateCurrentUser() {
      // await SMUser.updateCurrentUser();

      await app.$root.$emit("success-user-validation-after-refresh");

      SMApp.checkAuthOnServer();

      modelValuePopupSuccess.value = false;
    }

    const licensedSponsorOptions = [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ];

    const valid = computed(() => {
      if (props.isPrepaidSL) return companyNameValid.value && !!companyName.value;

      return companyNameValid.value && !!companyName.value && ifSponsor.value !== null;
    });

    return {
      modelValuePopupBanner,
      modelValuePopupSuccess,
      title,
      descriptions,
      label,
      companyName,
      query,
      loading,
      buttonLoading,
      filteredItems,
      valid,
      companyNameValid,
      rules,
      licensedSponsorOptions,
      ifSponsor,
      onSearch,
      onClear,
      onNoMatchSelect,
      onSuccess,
      updateCurrentUser,
    };
  },
});
